import { keepPreviousData, useQueries } from '@tanstack/react-query';

import { HttpError } from '@revelio/data-access';

import { environment } from '../../environments/environment';
import { beaLabel } from './data/bea-industries';
import { buildDemoImpact } from './data/demo-impact';
import type { GeoLevel } from './types';

export const DISPOSITION_DIRECT = 'direct' as const;
export const DISPOSITION_INDIRECT = 'indirect' as const;
export const DISPOSITION_INDUCED = 'induced' as const;

export type Disposition =
  | typeof DISPOSITION_DIRECT
  | typeof DISPOSITION_INDIRECT
  | typeof DISPOSITION_INDUCED;

// ---------------------------------------------------------------------
// View model — what the page renders. The components never see the raw
// Economic Impact API shape: the hook normalises it into these.
// ---------------------------------------------------------------------

/** A direct/indirect/induced breakdown for one industry or county. */
export interface ImpactEffect {
  /** Industry BEA code (or, for a county, its FIPS). */
  code: string;
  label: string;
  direct: number;
  indirect: number;
  /** Always 0 today — the MRIO has no household block yet. */
  induced: number;
  total: number;
}

export interface ImpactCounty {
  fips: string;
  direct: number;
  indirect: number;
  induced: number;
  total: number;
  /** That county's top industries (already ranked). */
  industries: ImpactEffect[];
}

export interface ImpactResult {
  total: {
    direct: number;
    indirect: number;
    induced: number;
    total: number;
    multiplier: number;
  };
  /** Top industries within the primary county, ranked by total output. */
  industries: ImpactEffect[];
  /** Top counties nationally, ranked by total output. */
  counties: ImpactCounty[];
}

// ---------------------------------------------------------------------
// Raw Economic Impact API shape (the subset the page consumes). Proxied by
// dash-meta at /api/economic-impact/invocations (the upstream is VPN-only).
// ---------------------------------------------------------------------

interface ApiEffectBreakdown {
  direct_dollars: number;
  indirect_output: number;
  induced_output: number;
  total_output: number;
  multiplier: number | null;
}

interface ApiPrimaryIndustryImpact extends ApiEffectBreakdown {
  industry_code: string;
}

interface ApiCountyIndustryImpact extends ApiEffectBreakdown {
  industry_code: string;
}

interface ApiCountyImpact extends ApiEffectBreakdown {
  region_fips: string;
  industries: ApiCountyIndustryImpact[];
}

interface ApiTotalImpact {
  direct_dollars: number;
  indirect_output: number;
  induced_output: number;
  total_output: number;
  multiplier: number;
}

interface ApiImpactResponse {
  total_impact: ApiTotalImpact;
  primary_fips: string;
  primary_industries: ApiPrimaryIndustryImpact[];
  top_counties: ApiCountyImpact[];
}

/** A single-investment shock — the page sends one per scenario. */
interface InvocationRequest {
  primary_fips: string;
  fips: string;
  amount_dollars: number;
  industry_code: string;
}

const effectFrom = (e: ApiEffectBreakdown, code: string): ImpactEffect => ({
  code,
  label: beaLabel(code),
  direct: e.direct_dollars,
  indirect: e.indirect_output,
  induced: e.induced_output,
  total: e.total_output,
});

const ZERO_TOTAL = {
  direct: 0,
  indirect: 0,
  induced: 0,
  total: 0,
  multiplier: 0,
};

const parseResponse = (r: ApiImpactResponse): ImpactResult => {
  const counties = r.top_counties.map((c) => ({
    fips: c.region_fips,
    direct: c.direct_dollars,
    indirect: c.indirect_output,
    induced: c.induced_output,
    total: c.total_output,
    industries: c.industries.map((i) => effectFrom(i, i.industry_code)),
  }));
  // `total_impact` is the economy-wide (national) total — the full ripple
  // across every county. The page's headline is the impact ON the reporting
  // geography, which is that county's own row in `top_counties`.
  const primary = counties.find((c) => c.fips === r.primary_fips);
  return {
    total: primary
      ? {
          direct: primary.direct,
          indirect: primary.indirect,
          induced: primary.induced,
          total: primary.total,
          multiplier: primary.direct > 0 ? primary.total / primary.direct : 0,
        }
      : ZERO_TOTAL,
    industries: r.primary_industries.map((i) => effectFrom(i, i.industry_code)),
    counties,
  };
};

// dash-meta proxies this to the VPN-only MRIO API, forwarding the upstream
// status + body on error (see economic-impact.controller). Address dash-meta
// via DASH_META_API like the other dash-meta calls (sentiment, session-auth):
// in prod the SPA is a different origin from dash-meta, so a bare `/api/...`
// would hit the dashboard host instead. When DASH_META_API is empty (some
// local envs) it stays same-origin and the dev proxy still covers it.
const INVOCATIONS_URL = `${environment.DASH_META_API}/api/economic-impact/invocations`;

// The API errors as { error, message } where `message` is either a string
// (invalid_input / service_unavailable) or a list of Pydantic issues
// (validation_error). Surface whichever the user can act on.
const errorMessage = (
  payload: { error?: string; message?: unknown } | null
): string | undefined => {
  if (!payload) return undefined;
  if (typeof payload.message === 'string') return payload.message;
  if (Array.isArray(payload.message)) {
    const issues = payload.message
      .map((item) =>
        item && typeof item === 'object' && 'msg' in item
          ? String((item as { msg: unknown }).msg)
          : String(item)
      )
      .join('; ');
    return issues || payload.error;
  }
  return payload.error;
};

const fetchImpact = async (
  request: InvocationRequest
): Promise<ImpactResult> => {
  const response = await fetch(INVOCATIONS_URL, {
    method: 'POST',
    // Send the ory_kratos_session cookie: in prod the SPA is a different origin
    // from dash-meta, so without this the cross-origin POST drops cookies and
    // dash-meta's AuthCookieGuard 401s. (The endpoint bypasses CSRF, so no
    // x-surf-token-dash-meta header is needed — unlike the sentiment POST.)
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  const text = await response.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok) {
    const reason =
      errorMessage(json as { error?: string; message?: unknown } | null) ??
      response.statusText;
    throw new HttpError(reason, response.status, response.statusText);
  }
  if (!json) {
    // 200 but not JSON — almost always the SPA's index.html, i.e. the
    // /api/economic-impact proxy route isn't active (restart the dev server).
    throw new HttpError(
      'Economic Impact API returned a non-JSON response — the dash-meta proxy is likely not reachable (restart the dev server).',
      response.status,
      response.statusText
    );
  }
  return parseResponse(json as ApiImpactResponse);
};

// ---------------------------------------------------------------------
// Multi-scenario fan-out
// ---------------------------------------------------------------------

// The MRIO model is county-grained: every investment needs a 5-digit county
// FIPS. State grain isn't supported yet (it's coming) — add 'state' here to
// light it up once the API accepts a 2-digit state FIPS.
const SUPPORTED_LEVELS = new Set<GeoLevel>(['county']);

export interface ImpactScenarioInput {
  industryId: string; // BEA detail code → industry_code
  amount: number;
  // Each scenario carries its own geography, independent of any other scenario
  // and of the page's primary geo (see EconomicImpactPage).
  fips: string; // 5-digit county (or 2-digit state once supported)
  level: GeoLevel;
}

export interface UseImpactScenariosArgs {
  scenarios: ImpactScenarioInput[];
  /** Resolve scenarios from the deterministic demo dataset, no network. */
  demoMode?: boolean;
  /** Demo-only seed (e.g. the output geography) to re-roll the demo charts. */
  seed?: string;
}

export interface FailedScenario {
  industryId: string;
  error: Error;
}

export interface UnsupportedScenario {
  industryId: string;
  fips: string;
  level: GeoLevel;
}

export interface UseImpactScenariosResult {
  data?: ImpactResult;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: Error;
  failedScenarios: FailedScenario[];
  /** Scenarios whose geography grain the API can't model yet (e.g. states). */
  unsupportedScenarios: UnsupportedScenario[];
}

const accumulate = (into: Map<string, ImpactEffect>, e: ImpactEffect) => {
  const prev = into.get(e.code);
  if (prev) {
    prev.direct += e.direct;
    prev.indirect += e.indirect;
    prev.induced += e.induced;
    prev.total += e.total;
  } else {
    into.set(e.code, { ...e });
  }
};

const byTotalDesc = (a: { total: number }, b: { total: number }) =>
  b.total - a.total;

// Combine per-scenario solves: sum the topline, and add industries/counties by
// key so two scenarios in the same county (or industry) stack rather than
// duplicate. A single scenario passes through (already ranked by the API).
const mergeResults = (results: ImpactResult[]): ImpactResult => {
  if (results.length === 1) return results[0] as ImpactResult;
  const total = { direct: 0, indirect: 0, induced: 0, total: 0, multiplier: 0 };
  const industries = new Map<string, ImpactEffect>();
  const counties = new Map<
    string,
    Omit<ImpactCounty, 'industries'> & { byIndustry: Map<string, ImpactEffect> }
  >();
  for (const result of results) {
    total.direct += result.total.direct;
    total.indirect += result.total.indirect;
    total.induced += result.total.induced;
    total.total += result.total.total;
    for (const industry of result.industries) accumulate(industries, industry);
    for (const county of result.counties) {
      const entry = counties.get(county.fips) ?? {
        fips: county.fips,
        direct: 0,
        indirect: 0,
        induced: 0,
        total: 0,
        byIndustry: new Map<string, ImpactEffect>(),
      };
      entry.direct += county.direct;
      entry.indirect += county.indirect;
      entry.induced += county.induced;
      entry.total += county.total;
      for (const industry of county.industries) {
        accumulate(entry.byIndustry, industry);
      }
      counties.set(county.fips, entry);
    }
  }
  total.multiplier = total.direct > 0 ? total.total / total.direct : 0;
  return {
    total,
    industries: [...industries.values()].sort(byTotalDesc),
    counties: [...counties.values()]
      .map(({ byIndustry, ...rest }) => ({
        ...rest,
        industries: [...byIndustry.values()].sort(byTotalDesc),
      }))
      .sort(byTotalDesc),
  };
};

export const useImpactScenarios = (
  args: UseImpactScenariosArgs
): UseImpactScenariosResult => {
  const { scenarios, demoMode = false, seed } = args;

  const unsupportedScenarios: UnsupportedScenario[] = scenarios
    .filter((scenario) => !SUPPORTED_LEVELS.has(scenario.level))
    .map(({ industryId, fips, level }) => ({ industryId, fips, level }));
  const supported = scenarios.filter((scenario) =>
    SUPPORTED_LEVELS.has(scenario.level)
  );
  const enabled = supported.length > 0;

  const queries = useQueries({
    queries: supported.map((scenario) => {
      const request: InvocationRequest = {
        // Single-investment shock: rank primary industries by the same county
        // the investment lands in.
        primary_fips: scenario.fips,
        fips: scenario.fips,
        amount_dollars: scenario.amount,
        industry_code: scenario.industryId,
      };
      return {
        queryKey: [
          'economic-impact',
          request,
          { demoMode, seed: demoMode ? seed : undefined },
        ],
        queryFn: (): Promise<ImpactResult> =>
          demoMode
            ? Promise.resolve(
                buildDemoImpact({
                  fips: scenario.fips,
                  level: scenario.level,
                  industryId: scenario.industryId,
                  amount: scenario.amount,
                  seed,
                })
              )
            : fetchImpact(request),
        enabled,
        // Keep the prior charts on screen while the re-rolled data resolves
        // (e.g. when the output geography changes) so they transition instead
        // of flashing empty.
        placeholderData: keepPreviousData,
      };
    }),
  });

  const isFetching = queries.some((q) => q.isFetching);
  const isLoading = queries.some((q) => q.isLoading);
  const successData: ImpactResult[] = [];
  const failedScenarios: FailedScenario[] = [];
  queries.forEach((q, i) => {
    const scenario = supported[i];
    if (!scenario) return;
    if (q.isError) {
      failedScenarios.push({
        industryId: scenario.industryId,
        error: (q.error as Error) ?? new Error('Unknown error'),
      });
    } else if (q.isSuccess && q.data) {
      // keepPreviousData widens useQueries' inferred data type; the resolved
      // shape is always ImpactResult (queryFn returns one).
      successData.push(q.data as ImpactResult);
    }
  });

  const allFailed =
    enabled &&
    queries.length > 0 &&
    successData.length === 0 &&
    failedScenarios.length === queries.length;

  const firstError = failedScenarios[0]?.error;
  return {
    data: successData.length > 0 ? mergeResults(successData) : undefined,
    isLoading,
    isFetching,
    isError: allFailed,
    error: allFailed ? firstError : undefined,
    failedScenarios,
    unsupportedScenarios,
  };
};
